import React, { useState, useEffect, useMemo, useCallback } from 'react';
import {
    Container,
    Box,
    Typography,
    Button,
    Paper,
    CircularProgress,
    Alert
} from '@mui/material';
import { useAuth } from '../contexts/AuthContext';
import FilterSidebar from '../components/FilterSidebar';
import axios from 'axios';
import { useLocation } from 'react-router-dom';
import SearchIcon from '@mui/icons-material/Search';
import PriceMatrix from '../components/PriceMatrix';
import ProductCard from '../components/ProductCard';
import { API_URL } from '../config';
import { useTheme } from '@mui/material/styles';
import useMediaQuery from '@mui/material/useMediaQuery';
import FilterListIcon from '@mui/icons-material/FilterList';
import COUNTRIES from '../constants/countries';
import { parsePriceString, createCurrencyFormatter } from '../utils/currency';

const findCountryByCode = (code) => COUNTRIES.find((country) => country.code === code);
const findCountryByName = (name) => COUNTRIES.find(
    (country) => country.name.toLowerCase() === name.toLowerCase()
);

// Helper to bucket ratings (must be before usage)
const getRatingBucket = (rating) => {
    if (rating >= 4.5) return 'High';
    if (rating >= 3.5) return 'Medium';
    return 'Low';
};

const Search = () => {
    const [image, setImage] = useState(null);
    const [imagePreview, setImagePreview] = useState(null);
    const [zip, setZip] = useState('');
    const [countryCode, setCountryCode] = useState('US');
    const [sessionName, setSessionName] = useState('');
    const [searchResults, setSearchResults] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [savedItems, setSavedItems] = useState([]);
    const [filters, setFilters] = useState({
        price: 1000,
        priceRange: [0, 1000],
        rating: 0,
        stores: []
    });
    const [filtersCollapsed, setFiltersCollapsed] = useState(false);
    const { isAuthenticated } = useAuth();
    const location = useLocation();
    const [matrixFilters, setMatrixFilters] = useState({ selectedDates: [], selectedRatings: [] });
    const [mobileFiltersOpen, setMobileFiltersOpen] = useState(true);
    const selectedCountry = useMemo(() => findCountryByCode(countryCode) || COUNTRIES[0], [countryCode]);
    const countryName = selectedCountry?.name || 'United States';
    const currencyCode = selectedCountry?.currency || 'USD';
    const currencyLocale = selectedCountry?.locale || 'en-US';
    const currencyFormatter = useMemo(
        () => createCurrencyFormatter(currencyLocale, currencyCode),
        [currencyLocale, currencyCode]
    );
    const formatCurrencyValue = useCallback(
        (value) => currencyFormatter.format(value ?? 0),
        [currencyFormatter]
    );
    const normalizeResults = useCallback((results) => {
        if (!Array.isArray(results)) return [];
        return results.map((item) => ({
            ...item,
            numericPrice: typeof item?.numericPrice === 'number'
                ? item.numericPrice
                : parsePriceString(item?.price),
        }));
    }, []);
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('md'));

    useEffect(() => {
        if (isAuthenticated) {
            fetchSavedItems();
        }
    }, [isAuthenticated]);

    // Helper function to detect if string is a URL
    const isValidUrl = (str) => {
        if (!str || typeof str !== 'string') return false;
        const trimmed = str.trim();
        try {
            if (trimmed.startsWith('http://') || trimmed.startsWith('https://')) {
                new URL(trimmed);
                return true;
            }
            // Check if it looks like a URL without protocol
            const urlPattern = /^([\da-z.-]+)\.([a-z.]{2,6})([/\w .-]*)*\/?$/i;
            if (urlPattern.test(trimmed) && trimmed.includes('.')) {
                const parts = trimmed.split('.');
                if (parts.length >= 2) {
                    const lastPart = parts[parts.length - 1].split('/')[0];
                    if (/^[a-z]{2,6}$/i.test(lastPart)) {
                        return true;
                    }
                }
            }
        } catch (e) {
            return false;
        }
        return false;
    };

    useEffect(() => {
        if (location.state && location.state.zip) {
            const hasImage = location.state.image;
            const hasSearchText = location.state.searchText && location.state.searchText.trim();
            
            if (hasImage || hasSearchText) {
                if (hasImage) {
                    setImage(location.state.image);
                    setImagePreview(URL.createObjectURL(location.state.image));
                }
                setZip(location.state.zip);
                if (location.state.countryCode) {
                    const incomingCode = location.state.countryCode.toUpperCase();
                    if (findCountryByCode(incomingCode)) {
                        setCountryCode(incomingCode);
                    }
                } else if (location.state.country) {
                    const matchedCountry = findCountryByName(location.state.country);
                    if (matchedCountry) {
                        setCountryCode(matchedCountry.code);
                    }
                }
                setSessionName(location.state.searchText || location.state.sessionName || '');
                setTimeout(() => {
                    handleSearchFromState(
                        location.state.image,
                        location.state.zip,
                        location.state.searchText || '',
                        location.state.sessionName || 'Search',
                        location.state.country || countryName,
                        location.state.countryCode || countryCode
                    );
                }, 0);
            }
        }
        // Intentionally only re-run when navigation state changes (avoid re-fetch loops on country/handler identity).
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [location.state]);

    const handleSearchFromState = async (img, z, searchText, sName, countryValue = countryName, countryCodeValue = countryCode) => {
        if (!z) {
            setError('Please enter a ZIP code.');
            return;
        }
        if (!img && !searchText.trim()) {
            setError('Please provide an image, text description, or link.');
            return;
        }
        setLoading(true);
        setError('');
        const formData = new FormData();
        const normalizedCountry = (countryValue || 'United States').trim();
        const normalizedCountryCode = (countryCodeValue || '').trim().toUpperCase() 
            || findCountryByName(normalizedCountry)?.code 
            || 'US';
        
        // Add image if provided
        if (img) {
            formData.append('image', img);
        }
        
        // Add zip code
        formData.append('zip', z);
        formData.append('country', normalizedCountry);
        formData.append('countryCode', normalizedCountryCode);
        
        // Determine if searchText is a URL or plain text
        const isLink = isValidUrl(searchText);
        if (isLink) {
            formData.append('link', searchText.trim());
        } else if (searchText.trim()) {
            formData.append('searchText', searchText.trim());
        }
        
        try {
            const response = await fetch(`${API_URL}/api/search`, {
                method: 'POST',
                body: formData
            });
            if (!response.ok) {
                const errorData = await response.json().catch(() => ({}));
                throw new Error(errorData.error || 'Search failed');
            }
            const data = await response.json();
            const normalizedResults = normalizeResults(data.results || []);
            setSearchResults(normalizedResults);

            // Log the enhanced results with extracted links for debugging
            if (data.results && data.results.length > 0) {
                if (process.env.NODE_ENV !== 'production') {
                    console.log(`📊 Total results received: ${data.results.length}`);
                }
                const resultsWithExtractedLinks = data.results.filter(result => result.extractedProductLink);
                if (process.env.NODE_ENV !== 'production') {
                    console.log(`🔗 Results with extracted links: ${resultsWithExtractedLinks.length}`);
                    data.results.forEach((result, index) => {
                        if (result.extractedProductLink) {
                            console.log(`✅ ${index + 1}. "${result.title}"`);
                            console.log(`   Original: ${result.link}`);
                            console.log(`   Extracted: ${result.extractedProductLink}`);
                        } else {
                            console.log(`❌ ${index + 1}. "${result.title}" - No extracted link`);
                            console.log(`   Original: ${result.link}`);
                        }
                    });
                }
            }
        } catch (err) {
            setError(err.message || 'Failed to perform search. Please try again.');
            setSearchResults([]);
        } finally {
            setLoading(false);
        }
    };

    const fetchSavedItems = async () => {
        try {
            const token = localStorage.getItem('token');
            if (!token) return;

            const response = await axios.get(`${API_URL}/api/user/saved`, {
                headers: { 'x-auth-token': token }
            });
            setSavedItems(response.data || []);
        } catch (err) {
            console.error('Error fetching saved items:', err);
            setError('Failed to load saved items');
        }
    };

    const handleFileSelect = (e) => {
        const file = e.target.files[0];
        if (file && file.type.startsWith('image/')) {
            setImage(file);
            setImagePreview(URL.createObjectURL(file));
        }
    };

    const handleSearch = async () => {
        if (!zip) {
            setError('Please enter a ZIP code.');
            return;
        }
        if (!countryCode) {
            setError('Please enter a country.');
            return;
        }
        if (!image && !sessionName.trim()) {
            setError('Please upload an image or paste a link, search, or description.');
            return;
        }

        setLoading(true);
        setError('');

        const formData = new FormData();
        if (image) {
            formData.append('image', image);
        }
        formData.append('zip', zip);
        formData.append('country', countryName);
        formData.append('countryCode', countryCode);

        const trimmedText = sessionName.trim();
        if (trimmedText) {
            if (isValidUrl(trimmedText)) {
                formData.append('link', trimmedText);
            } else {
                formData.append('searchText', trimmedText);
            }
        }

        try {
            const response = await fetch(`${API_URL}/api/search`, {
                method: 'POST',
                body: formData
            });

            if (!response.ok) {
                throw new Error('Search failed');
            }

            const data = await response.json();
            const normalizedResults = normalizeResults(data.results || []);
            setSearchResults(normalizedResults);

            if (data.results && data.results.length > 0) {
                if (process.env.NODE_ENV !== 'production') {
                    console.log(`📊 Total results received: ${data.results.length}`);
                }
                const resultsWithExtractedLinks = data.results.filter(result => result.extractedProductLink);
                if (process.env.NODE_ENV !== 'production') {
                    console.log(`🔗 Results with extracted links: ${resultsWithExtractedLinks.length}`);
                    data.results.forEach((result, index) => {
                        if (result.extractedProductLink) {
                            console.log(`✅ ${index + 1}. "${result.title}"`);
                            console.log(`   Original: ${result.link}`);
                            console.log(`   Extracted: ${result.extractedProductLink}`);
                        } else {
                            console.log(`❌ ${index + 1}. "${result.title}" - No extracted link`);
                            console.log(`   Original: ${result.link}`);
                        }
                    });
                }
            }
        } catch (err) {
            setError('Failed to perform search. Please try again.');
            console.error('Search error:', err);
            setSearchResults([]);
        } finally {
            setLoading(false);
        }
    };

    const handleToggleSave = async (item) => {
        if (!isAuthenticated) {
            setError('Please log in to save items');
            return;
        }

        try {
            const token = localStorage.getItem('token');
            if (!token) {
                throw new Error('No authentication token found');
            }

            if (process.env.NODE_ENV !== 'production') {
                console.log('Current saved items:', savedItems);
                console.log('Item to toggle:', item);
            }

            const isSaved = savedItems.some(savedItem => savedItem.link === item.link);
            if (process.env.NODE_ENV !== 'production') {
                console.log('Is item saved?', isSaved);
            }

            let response;
            if (isSaved) {
                // Remove item
                if (process.env.NODE_ENV !== 'production') {
                    console.log('Removing item...');
                }
                response = await axios.delete(`${API_URL}/api/user/saved`, {
                    headers: {
                        'x-auth-token': token,
                        'Content-Type': 'application/json'
                    },
                    data: { link: item.link }
                });
            } else {
                // Save item
                if (process.env.NODE_ENV !== 'production') {
                    console.log('Saving item...');
                }
                response = await axios.post(`${API_URL}/api/user/saved`, item, {
                    headers: {
                        'x-auth-token': token,
                        'Content-Type': 'application/json'
                    }
                });
            }

            if (process.env.NODE_ENV !== 'production') {
                console.log('Server response:', response.data);
            }

            // Update saved items with the response data
            setSavedItems(response.data);
            setError(null);
        } catch (err) {
            console.error('Error saving/removing item:', err);
            setError(err.response?.data?.message || 'Failed to save item. Please try again.');
        }
    };

    const filteredResults = searchResults.filter(item => {
        if (!item) return false;
        const parsedPrice = typeof item.numericPrice === 'number' ? item.numericPrice : parsePriceString(item.price);
        const comparablePrice = parsedPrice ?? Number.MAX_VALUE;
        const priceLimit = typeof filters.price === 'number' ? filters.price : Number.MAX_VALUE;
        const itemRating = item.rating || 0;
        const storeMatch = filters.stores.length === 0 || filters.stores.includes(item.source);
        return (
            comparablePrice <= priceLimit &&
            itemRating >= filters.rating &&
            storeMatch
        );
    });

    // Filter results by matrix checkboxes
    const matrixFilteredResults = filteredResults.filter(item => {
        // Use fake delivery date logic if missing
        const FAKE_DATES = ['05/05-11/05', '12/05-18/05', '19/05-25/05', '26/05-01/06', '02/06-08/06'];
        let deliveryDate = item.deliveryDate;
        if (!deliveryDate) {
            const idx = filteredResults.indexOf(item) % FAKE_DATES.length;
            deliveryDate = FAKE_DATES[idx];
        }
        const ratingBucket = getRatingBucket(item.rating || 0);
        return (
            (matrixFilters.selectedDates.length === 0 || matrixFilters.selectedDates.includes(deliveryDate)) &&
            (matrixFilters.selectedRatings.length === 0 || matrixFilters.selectedRatings.includes(ratingBucket))
        );
    });

    return (
        <Container maxWidth="lg">
            <Box sx={{ mt: 4 }}>
                {/* Streamlined horizontal search bar */}
                <Paper
                    elevation={0}
                    sx={{
                        width: '100%',
                        p: { xs: 2, sm: 3 },
                        mb: 3,
                        borderRadius: 4,
                        boxShadow: '0 8px 32px 0 rgba(31, 38, 135, 0.10)',
                        background: 'rgba(255,255,255,0.18)',
                        backdropFilter: 'blur(12px) saturate(180%)',
                        WebkitBackdropFilter: 'blur(12px) saturate(180%)',
                        border: '1px solid rgba(255,255,255,0.28)',
                    }}
                >
                    <Box
                        sx={{
                            display: 'grid',
                            gap: { xs: 1.5, sm: 2 },
                            gridTemplateColumns: {
                                xs: '56px minmax(0,1fr) 56px',
                                sm: '64px minmax(0,1fr) 140px 140px 64px',
                                md: '64px minmax(0,1fr) 160px 160px 64px',
                            },
                            gridTemplateAreas: {
                                xs: `"upload text search" "zip text ." "country country country"`,
                                sm: `"upload text zip country search"`,
                                md: `"upload text zip country search"`,
                            },
                            alignItems: 'center',
                        }}
                    >
                        {/* Image upload/preview */}
                        <Box sx={{ position: 'relative', gridArea: 'upload', width: { xs: 56, sm: 64 }, height: { xs: 56, sm: 64 } }}>
                            <input
                                type="file"
                                accept="image/*"
                                onChange={handleFileSelect}
                                style={{
                                    position: 'absolute',
                                    width: '100%',
                                    height: '100%',
                                    top: 0,
                                    left: 0,
                                    opacity: 0,
                                    cursor: 'pointer',
                                    zIndex: 2,
                                }}
                            />
                            <Box
                                sx={{
                                    width: '100%',
                                    height: '100%',
                                    borderRadius: 3,
                                    overflow: 'hidden',
                                    bgcolor: '#f0f0f0',
                                    border: '2px solid #e0e0e0',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    zIndex: 1,
                                    boxShadow: '0 4px 16px 0 rgba(30, 41, 59, 0.10)',
                                    transition: 'box-shadow 0.2s',
                                }}
                            >
                                {imagePreview ? (
                                    <img
                                        src={imagePreview}
                                        alt="Preview"
                                        style={{ width: '100%', height: '100%', objectFit: 'cover', borderRadius: 12 }}
                                    />
                                ) : (
                                    <Typography variant="caption" color="text.secondary" align="center">
                                        Upload
                                    </Typography>
                                )}
                            </Box>
                        </Box>

                        {/* Search name */}
                        <Box sx={{ gridArea: 'text', width: '100%' }}>
                            <Box
                                sx={{
                                    width: '100%',
                                    height: { xs: 48, sm: 52 },
                                    borderRadius: 999,
                                    background: 'rgba(255,255,255,0.7)',
                                    display: 'flex',
                                    alignItems: 'center',
                                    px: 2,
                                    fontWeight: 500,
                                    fontSize: { xs: '0.9rem', sm: '0.95rem' },
                                    color: 'text.primary',
                                    border: '1px solid rgba(0,0,0,0.1)',
                                }}
                            >
                                <input
                                    value={sessionName}
                                    onChange={(e) => setSessionName(e.target.value)}
                                    placeholder="Paste a screenshot of an existing product to match, or describe what you're looking for"
                                    style={{
                                        width: '100%',
                                        border: 'none',
                                        outline: 'none',
                                        background: 'transparent',
                                        fontWeight: 500,
                                        fontSize: 'inherit',
                                        color: 'inherit',
                                    }}
                                />
                            </Box>
                        </Box>

                        {/* Zip code */}
                        <Box sx={{ gridArea: 'zip', width: '100%' }}>
                            <Box
                                sx={{
                                    width: '100%',
                                    height: { xs: 48, sm: 52 },
                                    borderRadius: 999,
                                    background: 'rgba(255,255,255,0.7)',
                                    display: 'flex',
                                    alignItems: 'center',
                                    px: 2,
                                    fontWeight: 500,
                                    fontSize: { xs: '0.9rem', sm: '0.95rem' },
                                    color: zip ? 'text.primary' : 'text.secondary',
                                    border: '1px solid rgba(0,0,0,0.1)',
                                }}
                            >
                                <input
                                    value={zip}
                                    onChange={(e) => setZip(e.target.value)}
                                    placeholder="Zip Code"
                                    style={{
                                        width: '100%',
                                        border: 'none',
                                        outline: 'none',
                                        background: 'transparent',
                                        fontWeight: 500,
                                        fontSize: 'inherit',
                                    }}
                                />
                            </Box>
                        </Box>

                        {/* Country */}
                        <Box sx={{ gridArea: 'country', width: '100%' }}>
                            <Box
                                sx={{
                                    width: '100%',
                                    height: { xs: 48, sm: 52 },
                                    borderRadius: 999,
                                    background: 'rgba(255,255,255,0.7)',
                                    display: 'flex',
                                    alignItems: 'center',
                                    px: 2,
                                    fontWeight: 500,
                                    fontSize: { xs: '0.9rem', sm: '0.95rem' },
                                    color: countryCode ? 'text.primary' : 'text.secondary',
                                    border: '1px solid rgba(0,0,0,0.1)',
                                }}
                            >
                                <select
                                    value={countryCode}
                                    onChange={(e) => setCountryCode(e.target.value)}
                                    style={{
                                        width: '100%',
                                        border: 'none',
                                        outline: 'none',
                                        background: 'transparent',
                                        fontWeight: 500,
                                        fontSize: 'inherit',
                                        color: 'inherit',
                                        appearance: 'none',
                                        WebkitAppearance: 'none',
                                        MozAppearance: 'none',
                                        paddingRight: 16,
                                        cursor: 'pointer'
                                    }}
                                >
                                    {COUNTRIES.map((option) => (
                                        <option key={option.code} value={option.code}>
                                            {option.name}
                                        </option>
                                    ))}
                                </select>
                            </Box>
                        </Box>

                        {/* Search icon button */}
                        <Button
                            variant="contained"
                            onClick={handleSearch}
                            disabled={loading || !zip || !countryCode || (!image && !sessionName.trim())}
                            sx={{
                                gridArea: 'search',
                                width: { xs: 56, sm: 64 },
                                height: { xs: 56, sm: 64 },
                                minWidth: 'auto',
                                borderRadius: 3,
                                background: '#1976d2',
                                boxShadow: '0 2px 8px 0 rgba(25, 118, 210, 0.18)',
                                transition: 'background 0.2s, box-shadow 0.2s',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                p: 0,
                                '&:hover': {
                                    background: '#1565c0',
                                    boxShadow: '0 4px 16px 0 rgba(25, 118, 210, 0.22)',
                                },
                            }}
                        >
                            {loading ? <CircularProgress size={24} /> : <SearchIcon sx={{ color: 'white', fontSize: { xs: 24, sm: 28 } }} />}
                        </Button>
                    </Box>
                </Paper>

                {error && (
                    <Alert severity="error" sx={{ mb: 2 }}>
                        {error}
                    </Alert>
                )}

                {/* Only show PriceMatrix if there are search results */}
                {searchResults && searchResults.length > 0 && (
                    <PriceMatrix
                        results={filteredResults}
                        onFilterChange={setMatrixFilters}
                        formatPrice={formatCurrencyValue}
                    />
                )}


                {searchResults && searchResults.length > 0 && (
                    <Box
                        sx={{
                            display: 'flex',
                            gap: 2,
                            flexDirection: { xs: 'column', md: 'row' },
                            alignItems: { xs: 'stretch', md: 'flex-start' }
                        }}
                    >
                        {!isMobile && !filtersCollapsed && (
                            <FilterSidebar
                                allResults={searchResults}
                                filters={filters}
                                setFilters={setFilters}
                                onCollapse={() => setFiltersCollapsed(true)}
                                isMobile={false}
                                formatCurrency={formatCurrencyValue}
                                parsePrice={parsePriceString}
                            />
                        )}
                        <Box sx={{ flex: 1, width: '100%' }}>
                            {!isMobile && filtersCollapsed && (
                                <Button
                                    onClick={() => setFiltersCollapsed(false)}
                                    startIcon={<FilterListIcon />}
                                    sx={{ mb: 2 }}
                                >
                                    Show Filters
                                </Button>
                            )}
                            {isMobile && (
                                <Button
                                    variant="outlined"
                                    startIcon={<FilterListIcon />}
                                    sx={{ mb: 2, alignSelf: 'flex-start' }}
                                    onClick={() => setMobileFiltersOpen((prev) => !prev)}
                                >
                                    {mobileFiltersOpen ? 'Hide Filters' : 'Show Filters'}
                                </Button>
                            )}
                            {isMobile && mobileFiltersOpen && (
                                <Box sx={{ mb: 2 }}>
                                    <FilterSidebar
                                        allResults={searchResults}
                                        filters={filters}
                                        setFilters={setFilters}
                                        isMobile
                                        formatCurrency={formatCurrencyValue}
                                        parsePrice={parsePriceString}
                                    />
                                </Box>
                            )}
                            <Box>
                                {matrixFilteredResults.map((product, idx) => (
                                    <ProductCard
                                        key={product.link || idx}
                                        product={product}
                                        onToggleSave={handleToggleSave}
                                        savedItems={savedItems}
                                        formatPrice={formatCurrencyValue}
                                    />
                                ))}
                            </Box>
                        </Box>
                    </Box>
                )}
            </Box>
        </Container>
    );
};

export default Search; 